<template>
  <div class="billing-page">
    <div class="page-container">
      <header class="page-header">
        <h1 class="page-title">Billing</h1>
        <p class="page-subtitle">Manage your payment methods and view invoices</p>
      </header>

      <!-- Success Message -->
      <div v-if="showSuccessMessage" class="success-banner">
        <svg class="success-icon" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          />
        </svg>
        <div class="success-content">
          <h3 class="success-title">Thanks! Your edit credits have been added to your account.</h3>
          <p class="success-text">Your credits are ready to use on your next manuscript.</p>
        </div>
        <button @click="dismissSuccess" class="dismiss-button">×</button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading billing information...</p>
      </div>

      <template v-else>
        <!-- Edit Credits Section -->
        <div class="billing-section">
          <div class="section-header">
            <h2 class="section-title">Edit Credits</h2>
            <router-link to="/credits" class="buy-credits-button">Buy Credits</router-link>
          </div>

          <div class="credit-balance-card">
            <div class="balance-info">
              <div class="balance-label">Current Balance</div>
              <div class="balance-value">{{ creditBalance }} {{ creditBalance === 1 ? 'edit' : 'edits' }}</div>
            </div>
          </div>

          <div v-if="creditPurchases.length > 0" class="credit-purchases">
            <h3 class="subsection-title">Purchase History</h3>
            <div class="purchases-table">
              <div class="table-header">
                <div class="col-date">Date</div>
                <div class="col-package">Package</div>
                <div class="col-amount">Amount Paid</div>
                <div class="col-remaining">Credits Remaining</div>
              </div>
              <div v-for="purchase in creditPurchases" :key="purchase.id" class="table-row">
                <div class="col-date">{{ formatDate(new Date(purchase.purchaseDate).getTime() / 1000) }}</div>
                <div class="col-package">
                  {{ purchase.packageSize }} {{ purchase.packageSize === 1 ? 'edit' : 'edits' }}
                </div>
                <div class="col-amount">${{ (purchase.amountPaid / 100).toFixed(2) }}</div>
                <div class="col-remaining">{{ purchase.creditsRemaining }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Methods Section -->
        <div class="billing-section">
          <div class="section-header">
            <h2 class="section-title">Payment Methods</h2>
          </div>

          <div v-if="paymentMethods.length === 0" class="empty-message">
            <svg class="empty-icon" viewBox="0 0 20 20" fill="currentColor">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
              <path
                fill-rule="evenodd"
                d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                clip-rule="evenodd"
              />
            </svg>
            <p>Payment methods are added automatically when you purchase an edit.</p>
          </div>

          <div v-else class="payment-methods-list">
            <div v-for="method in paymentMethods" :key="method.id" class="payment-method-card">
              <div class="method-info">
                <div class="card-brand">{{ method.brand }}</div>
                <div class="card-number">•••• {{ method.last4 }}</div>
                <div class="card-expiry">Expires {{ method.exp_month }}/{{ method.exp_year }}</div>
              </div>
              <div class="method-actions">
                <span v-if="method.isDefault" class="default-badge">Default</span>
                <button v-else @click="setDefaultPaymentMethod(method.id)" class="action-link">Set as default</button>
                <button @click="removePaymentMethod(method.id)" class="remove-link">Remove</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Invoices Section -->
        <div class="billing-section">
          <div class="section-header">
            <h2 class="section-title">Invoices</h2>
          </div>

          <div v-if="invoices.length === 0" class="empty-message">
            <svg class="empty-icon" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
                clip-rule="evenodd"
              />
            </svg>
            <p>No invoices yet</p>
          </div>

          <div v-else class="invoices-table">
            <div class="table-header">
              <div class="col-date">Date</div>
              <div class="col-description">Description</div>
              <div class="col-amount">Amount</div>
              <div class="col-status">Status</div>
              <div class="col-actions">Download</div>
            </div>
            <div v-for="invoice in invoices" :key="invoice.id" class="table-row">
              <div class="col-date">{{ formatDate(invoice.created) }}</div>
              <div class="col-description">{{ invoice.description }}</div>
              <div class="col-amount">${{ (invoice.amount / 100).toFixed(2) }}</div>
              <div class="col-status">
                <span class="status-badge" :class="`status-${invoice.status}`">
                  {{ invoice.status }}
                </span>
              </div>
              <div class="col-actions">
                <a :href="invoice.invoice_pdf" target="_blank" class="download-link">
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  PDF
                </a>
              </div>
            </div>
          </div>
        </div>
      </template>

      <p v-if="error" class="error-message">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { fetchWithAuth } from '../services/api';
import { useAuthStore } from '../stores/auth';

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  isDefault: boolean;
}

interface Invoice {
  id: string;
  created: number;
  description: string;
  amount: number;
  status: string;
  invoice_pdf: string;
}

interface CreditPurchase {
  id: string;
  packageSize: number;
  amountPaid: number;
  creditsPurchased: number;
  creditsRemaining: number;
  purchaseDate: string;
}

const router = useRouter();
const authStore = useAuthStore();

const loading = ref(true);
const error = ref('');
const showSuccessMessage = ref(false);
const creditBalance = ref(0);
const creditPurchases = ref<CreditPurchase[]>([]);
const paymentMethods = ref<PaymentMethod[]>([]);
const invoices = ref<Invoice[]>([]);

function dismissSuccess() {
  showSuccessMessage.value = false;
  // Remove query param from URL
  router.replace({ query: {} });
}

async function loadCreditInfo() {
  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}/credits`);
    if (!response.ok) return;
    const data = await response.json();
    creditBalance.value = data.balance || 0;
    creditPurchases.value = data.purchases || [];
  } catch (err) {
    console.error('Failed to load credit info:', err);
  }
}

async function loadBillingInfo() {
  loading.value = true;
  error.value = '';

  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}/billing`);
    if (!response.ok) {
      throw new Error('Failed to load billing information');
    }
    const data = await response.json();
    paymentMethods.value = data.paymentMethods || [];
    invoices.value = data.invoices || [];
  } catch (err) {
    error.value = 'Failed to load billing information. Please try again.';
    console.error('Failed to load billing info:', err);
  } finally {
    loading.value = false;
  }
}

async function setDefaultPaymentMethod(methodId: string) {
  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}/payment-method`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentMethodId: methodId }),
    });

    if (!response.ok) {
      throw new Error('Failed to set default payment method');
    }

    await loadBillingInfo();
  } catch (err) {
    error.value = 'Failed to update payment method.';
    console.error('Set default payment method error:', err);
  }
}

async function removePaymentMethod(methodId: string) {
  if (!confirm('Are you sure you want to remove this payment method?')) {
    return;
  }

  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}/payment-method/${methodId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to remove payment method');
    }

    await loadBillingInfo();
  } catch (err) {
    error.value = 'Failed to remove payment method.';
    console.error('Remove payment method error:', err);
  }
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

onMounted(() => {
  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth', query: { redirect: '/billing' } });
  } else {
    // Check for success message
    if (router.currentRoute.value.query['purchase'] === 'success') {
      showSuccessMessage.value = true;
    }

    loadBillingInfo();
    loadCreditInfo();
  }
});
</script>

<style scoped>
.billing-page {
  flex: 1;
  background: #f7fafc;
}

.page-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.page-header {
  margin-bottom: 3rem;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.125rem;
  color: #718096;
}

/* Success Banner */
.success-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 2rem;
}

.success-icon {
  width: 32px;
  height: 32px;
  color: #16a34a;
  flex-shrink: 0;
}

.success-content {
  flex: 1;
}

.success-title {
  font-size: 1rem;
  font-weight: 600;
  color: #166534;
  margin-bottom: 0.25rem;
}

.success-text {
  font-size: 0.875rem;
  color: #15803d;
  margin: 0;
}

.dismiss-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #16a34a;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.dismiss-button:hover {
  background: rgba(22, 163, 74, 0.1);
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: #718096;
  font-size: 1rem;
}

/* Billing Sections */
.billing-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
}

.buy-credits-button {
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: var(--color-accent);
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.2s;
}

.buy-credits-button:hover {
  background: var(--color-accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(97, 174, 212, 0.3);
}

/* Credit Balance Card */
.credit-balance-card {
  background: linear-gradient(135deg, var(--color-accent) 0%, #4a9cbd 100%);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
}

.balance-info {
  text-align: center;
}

.balance-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.balance-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
}

/* Credit Purchases */
.credit-purchases {
  margin-top: 2rem;
}

.subsection-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 1rem;
}

.purchases-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.purchases-table .table-header {
  display: grid;
  grid-template-columns: 140px 1fr 140px 140px;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.purchases-table .table-row {
  display: grid;
  grid-template-columns: 140px 1fr 140px 140px;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.875rem;
  align-items: center;
}

.purchases-table .table-row:last-child {
  border-bottom: none;
}

.purchases-table .table-row:hover {
  background: #f7fafc;
}

.purchases-table .col-package {
  color: #1a202c;
  font-weight: 500;
}

.purchases-table .col-amount {
  color: #1a202c;
  font-weight: 600;
}

.purchases-table .col-remaining {
  color: var(--color-accent);
  font-weight: 600;
}

/* Empty Message */
.empty-message {
  text-align: center;
  padding: 3rem 2rem;
  color: #a0aec0;
}

.empty-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 1rem;
  color: #cbd5e0;
}

.empty-message p {
  font-size: 0.875rem;
}

/* Payment Methods List */
.payment-methods-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.payment-method-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: border-color 0.2s;
}

.payment-method-card:hover {
  border-color: #cbd5e0;
}

.method-info {
  flex: 1;
}

.card-brand {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a202c;
  text-transform: capitalize;
}

.card-number {
  font-size: 0.875rem;
  color: #4a5568;
  margin-top: 0.25rem;
}

.card-expiry {
  font-size: 0.75rem;
  color: #a0aec0;
  margin-top: 0.25rem;
}

.method-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.default-badge {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #22543d;
  background: #c6f6d5;
  border-radius: 12px;
}

.action-link,
.remove-link {
  background: none;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;
  font-family: inherit;
}

.action-link {
  color: var(--color-accent);
}

.action-link:hover {
  color: var(--color-accent-hover);
}

.remove-link {
  color: #e53e3e;
}

.remove-link:hover {
  color: #c53030;
}

/* Invoices Table */
.invoices-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 120px 1fr 100px 100px 100px;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.table-row {
  display: grid;
  grid-template-columns: 120px 1fr 100px 100px 100px;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.875rem;
  align-items: center;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: #f7fafc;
}

.col-date {
  color: #4a5568;
}

.col-description {
  color: #1a202c;
  font-weight: 500;
}

.col-amount {
  color: #1a202c;
  font-weight: 600;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 12px;
  text-transform: capitalize;
}

.status-paid {
  color: #22543d;
  background: #c6f6d5;
}

.status-open {
  color: #975a16;
  background: #fef5e7;
}

.status-void {
  color: #718096;
  background: #e2e8f0;
}

.download-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--color-accent);
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;
}

.download-link:hover {
  color: var(--color-accent-hover);
}

.download-link svg {
  width: 16px;
  height: 16px;
}

.error-message {
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  font-weight: 500;
}

@media (max-width: 768px) {
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .table-header {
    display: none;
  }

  .table-row {
    padding: 1.25rem;
  }

  .table-row > div::before {
    content: attr(data-label);
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.25rem;
  }
}
</style>
